import React from 'react';
import { colors } from '../../../theme';
import { formatDollars } from '../../../utils/straddleCalculations';
import {
  CUSTOM_SPLIT,
  SPLIT_PRESETS,
  describeBudget,
  resolveBudgets,
  setBudgetTotal,
  setCustomBudget,
  sizingProblem,
  splitLabel,
  toggleSplit,
} from '../../../utils/straddleMappings';
import { TIP_COST, rowTip } from './StraddleRowTooltip';

const labelStyle = { fontSize: 12, fontWeight: 500, color: '#d1d5db', whiteSpace: 'nowrap' };

const inputStyle = {
  width: 96,
  padding: '4px 8px',
  borderRadius: 6,
  border: `1px solid ${colors.border}`,
  background: '#1f2937',
  color: colors.textPrimary,
  fontSize: 14,
};

/** A dollar box. Stays a number input so phones show the number pad. */
function MoneyInput({ label, ariaLabel, value, onChange }) {
  return (
    <label className="flex items-center gap-2" style={labelStyle}>
      {label}
      <span className="flex items-center gap-1">
        <span aria-hidden="true">$</span>
        <input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          aria-label={ariaLabel}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      </span>
    </label>
  );
}

function SplitButton({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`tab-button ${selected ? 'tab-selected' : 'tab-unselected'}`}
    >
      {label}
    </button>
  );
}

/**
 * How much money goes on each side, under any number of call/put splits at once.
 * Each split gives every straddle its own row, so they can be compared directly.
 *
 * Controlled, because it's rendered twice from one state: in the form, and above
 * the results so a budget can be tuned while watching the numbers. Sizing needs
 * nothing from the server, so a change reprices instantly with no re-run.
 *
 * @param {Array} hiddenBudgets selected splits left out to stay under the row limit
 */
function StraddleSizingControls({ sizing, onChange, hiddenBudgets = [] }) {
  const budgets = resolveBudgets(sizing);
  const problem = sizingProblem(sizing);
  const customSelected = sizing.splits.includes(CUSTOM_SPLIT);

  return (
    <div className="space-y-3 text-left">
      <div className="flex flex-wrap items-center gap-2">
        <span style={labelStyle}>Position size</span>
        <SplitButton
          label="1 contract each"
          selected={!sizing.enabled}
          onClick={() => onChange({ ...sizing, enabled: false })}
        />
        <SplitButton
          label="By budget"
          selected={sizing.enabled}
          onClick={() => onChange({ ...sizing, enabled: true })}
        />
      </div>

      {sizing.enabled && (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <MoneyInput
              label="Total"
              ariaLabel="Total budget"
              value={sizing.total}
              onChange={(raw) => onChange(setBudgetTotal(sizing, raw))}
            />
            <div className="flex flex-wrap items-center gap-2">
              <span style={labelStyle}>Call / Put, pick any</span>
              {SPLIT_PRESETS.map((preset) => (
                <SplitButton
                  key={preset.key}
                  label={splitLabel(preset)}
                  selected={sizing.splits.includes(preset.key)}
                  onClick={() => onChange(toggleSplit(sizing, preset.key))}
                />
              ))}
              <SplitButton
                label="Custom"
                selected={customSelected}
                onClick={() => onChange(toggleSplit(sizing, CUSTOM_SPLIT))}
              />
            </div>
          </div>

          {customSelected && (
            <div className="flex flex-wrap items-center gap-4">
              <MoneyInput
                label="Custom call"
                ariaLabel="Custom call budget"
                value={sizing.customCall}
                onChange={(raw) => onChange(setCustomBudget(sizing, 'call', raw))}
              />
              <MoneyInput
                label="Custom put"
                ariaLabel="Custom put budget"
                value={sizing.customPut}
                onChange={(raw) => onChange(setCustomBudget(sizing, 'put', raw))}
              />
            </div>
          )}

          {budgets.length > 0 && (
            <ul className="text-xs text-gray-300 space-y-0.5">
              {budgets.map((budget) => <li key={budget.key}>{describeBudget(budget)}</li>)}
            </ul>
          )}
          {problem && (
            <p className="text-xs text-yellow-400">
              {problem}
              {budgets.length === 0 && ' Until then, each row is priced at 1 contract each.'}
            </p>
          )}
          {hiddenBudgets.length > 0 && (
            <p className="text-xs text-yellow-400">
              Too many rows to show them all, so these splits are left out:{' '}
              {hiddenBudgets.map((budget) => budget.label).join(', ')}.
              Run fewer straddles to compare more splits.
            </p>
          )}
          <p className="text-xs text-gray-400">
            Each split adds its own row for every straddle. Each side rounds to the nearest whole
            contract, so it can spend a little more or less than this. If one contract costs more
            than a side&apos;s budget, the row buys one anyway. Percent gains are on what the row
            actually spends.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * The Cost column: what the position costs, and with a budget set, how many of each
 * contract that bought, plus which split when there's more than one to tell apart.
 * Amber when it spends more than that split's total.
 */
export function StraddleCostCell({ row, tooltipId }) {
  const size = row._size;
  if (!size) return formatDollars(row.Cost);
  return (
    <div style={{ fontSize: 11, lineHeight: 1.3 }} {...rowTip(tooltipId, row, TIP_COST)}>
      <div style={size.overBudget ? { color: colors.warning } : undefined}>
        {formatDollars(row.Cost)}
      </div>
      {/* Wraps at the space rather than overflowing this fixed-width sticky column. */}
      <div className="text-gray-400" style={{ whiteSpace: 'normal' }}>
        {size.call.contracts}C {size.put.contracts}P
      </div>
      {size.showSplit && <div style={{ color: colors.primary }}>{size.label}</div>}
    </div>
  );
}

export default StraddleSizingControls;
